import { Component } from "preact";
/** @import { ComponentChildren } from "preact"; */

import announce from "./announce.js";

/** @type {(title: string) => string} A function to "decorate" title changes. */
let decorate = title => title;

/**
 * @type {Component<Props>[]} The stack of <Title> components, the last of which is the currently
 *   applied title.
 */
const titles = [];

/** @type {Set<(title: string) => void>} A set of listeners to be notified when the title changes. */
const listeners = new Set();

/** @param {(title: string) => void} fn Listen for title changes. */
export function onTitleChange(fn) {
	listeners.add(fn);
	return () => listeners.delete(fn);
}

/** @param {(title: string) => string} fn Set a function to decorate title changes. */
export function setTitleDecoration(fn) {
	decorate = fn;
}

/** Whether a title change is queued. */
let queued = false;

/** Queue a title update. */
function updateTitle() {
	// ensure we don't queue more than a single title update per render
	if (queued) return;
	queued = true;

	// queue a microtask to update the title after rendering is finished
	queueMicrotask(() => {
		queued = false;
		const last = titles.at(-1);
		if (!last || last.props.title === document.title) return;

		// decorate the component's title and set the document title
		document.title = decorate(last.props.title);

		// announce the new title to screen readers
		announce(document.title, { level: "assertive" });

		// notify any listners of the change
		for (const listener of listeners) listener(document.title);
	});
}

/**
 * @typedef {object} Props
 * @property {string} title
 * @property {ComponentChildren} [children]
 */

/** @extends {Component<Props>} */
export default class Title extends Component {
	mounted = false;

	/**
	 * @param {Props} prev
	 * @override
	 */
	componentDidUpdate(prev) {
		if (prev.title !== this.props.title) updateTitle();
	}

	/** @override */
	componentWillUnmount() {
		const index = titles.indexOf(this);
		if (index === -1) return;

		titles.splice(index, 1);
		updateTitle();
	}

	render() {
		// componentDidMount gets called for inner components before outer ones,
		// so instead we add the title to the stack in `render` (which gets called outside-in)
		// and set a bit to prevent a component from being added more than once
		if (!this.mounted) {
			titles.push(this);
			this.mounted = true;
			updateTitle();
		}

		return this.props.children;
	}
}
